export const SESSION_REFRESH_TITLE = 'Session expired';
export const SESSION_REFRESH_MESSAGE = 'Your session needs to be refreshed. Please reload the page to continue.';
export const AUTH_EXPIRED_EVENT = 'auth-expired';
const FORCE_AUTH_EXPIRED_MODE_KEY = 'forceAuthExpiredMode';
const FORCE_AUTH_EXPIRED_MODE_FAIL = 'fail';
const AUTH_EXPIRED_THROTTLE_MS = 15000;
let lastAuthExpiredAt = 0;
let authExpiredDialogVisible = false;

const AUTH_EXPIRED_STATUS_CODES = new Set([401, 403]);
const AUTH_EXPIRED_MESSAGE_SNIPPETS = [
    'token expired',
    'jwt expired',
    'expired token',
    'unauthorized',
    'not authorized',
    'no current user',
    'invalid token',
    'token is not valid',
    'token is invalid',
];

export const isAuthExpiredResponse = (response, fallbackMessage = '') => {
    const status = response?.status;
    const message = `${response?.data?.message || response?.statusText || fallbackMessage || ''}`.toLowerCase();

    if (status === 401) {
        return true;
    }

    if (AUTH_EXPIRED_STATUS_CODES.has(status)) {
        return AUTH_EXPIRED_MESSAGE_SNIPPETS.some((snippet) => message.includes(snippet));
    }

    if (message) {
        return AUTH_EXPIRED_MESSAGE_SNIPPETS.some((snippet) => message.includes(snippet));
    }

    return false;
};

export const notifyAuthExpired = (detail = {}) => {
    if (typeof window === 'undefined') {
        return;
    }

    const now = Date.now();
    if (now - lastAuthExpiredAt < AUTH_EXPIRED_THROTTLE_MS) {
        return;
    }

    lastAuthExpiredAt = now;
    authExpiredDialogVisible = true;
    try {
        window.__authExpiredDialogVisible = true;
    } catch (error) {
        // ignore
    }
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail }));
};

export const setAuthExpiredDialogVisible = (visible) => {
    authExpiredDialogVisible = visible;
    if (!visible) {
        lastAuthExpiredAt = 0;
    }
    try {
        window.__authExpiredDialogVisible = visible;
    } catch (error) {
        // ignore
    }
};

export const isAuthExpiredDialogVisible = () => {
    if (authExpiredDialogVisible) {
        return true;
    }

    try {
        return Boolean(window?.__authExpiredDialogVisible);
    } catch (error) {
        return false;
    }
};

export const subscribeAuthExpired = (handler) => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
};

const getNestedError = (error) => error?.originalError || error?.cause || null;

const hasAuthHeader = (config) => {
    const headers = config?.headers || {};
    return Boolean(headers.Authorization || headers.authorization);
};

const isAuthNetworkError = (error) => {
    const message = `${error?.message || ''}`.toLowerCase();
    if (message !== 'network error') {
        return false;
    }

    if (hasAuthHeader(error?.config)) {
        return true;
    }

    const nested = getNestedError(error);
    return Boolean(nested && hasAuthHeader(nested?.config));
};

export const isAuthExpiredOverrideActive = () => {
    if (process.env.NODE_ENV === 'production') {
        return false;
    }

    try {
        return localStorage.getItem('forceAuthExpired') === '1';
    } catch (error) {
        return false;
    }
};

export const isAuthExpiredForceFailActive = () => {
    if (process.env.NODE_ENV === 'production') {
        return false;
    }

    try {
        return localStorage.getItem(FORCE_AUTH_EXPIRED_MODE_KEY) === FORCE_AUTH_EXPIRED_MODE_FAIL;
    } catch (error) {
        return false;
    }
};

export const isAuthExpiredError = (error) => {
    if (isAuthExpiredOverrideActive()) {
        return true;
    }

    if (error?.isAuthExpired) {
        return true;
    }

    if (isAuthExpiredResponse(error?.response, error?.message)) {
        return true;
    }

    const nested = getNestedError(error);
    if (nested && isAuthExpiredResponse(nested?.response, nested?.message)) {
        return true;
    }

    return isAuthNetworkError(error);
};
